import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
// Removed: import { useDomainStore } from '~/stores/domain-store';
import type { Tld } from '~/config/tld-config'; // Keep for settings
import { useSearchSettings } from '~/hooks/use-search-settings';
import {
  DEFAULT_SETTINGS,
  type SearchSettings,
} from '~/config/search-settings';
import { clearSettingsFromStorage } from '~/lib/settings-storage';
import { searchService } from '~/services/search-service'; // Import the singleton instance
import type { DomainDescription } from '~/types/domain'; // Import DomainDescription

// ----------------------------------------------------------
// Refactored Context Type
// ----------------------------------------------------------
export interface SearchContextType {
  // UI Input State
  query: string;
  setQuery: (query: string) => void;
  showUnavailable: boolean;
  setShowUnavailable: (show: boolean) => void;
  searchExecuted: boolean; // Still useful for UI logic (e.g., showing results grid)
  setSearchExecuted: (value: boolean) => void;

  // Settings State (from hook)
  settings: SearchSettings;
  setMode: (mode: typeof DEFAULT_SETTINGS.mode) => void;
  setMaxLength: (length: number) => void;
  setSelectedTlds: (tlds: Tld[]) => void;
  setCreativityLevel: (level: typeof DEFAULT_SETTINGS.creativityLevel) => void;
  setTldMode: (mode: 'auto' | 'explicit') => void;
  setDomainLanguage: (lang: string) => void;
  setAvoidExpensiveTlds: (avoid: boolean) => void;
  hasCustomSettings: boolean;

  // Note: Loading/error/results state should be read directly via useDomainStore in components

  // Actions Delegated to SearchService or Settings Hook
  executeSearch: (submittedQuery?: string) => Promise<void>;
  cancelSearch: () => void;
  clearCurrentSearch: () => void;
  resetSettingsToDefaults: () => void;
  fetchDomainDescription: (
    domainName: string,
    language: string,
    userQuery: string
  ) => Promise<DomainDescription>; // Add description fetcher
}

export const SearchContext = createContext<SearchContextType>(
  {} as SearchContextType
);

// ----------------------------------------------------------
// Refactored Provider
// ----------------------------------------------------------
export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  // --- UI Input State ---
  const [query, setQuery] = useState('');
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [searchExecuted, setSearchExecuted] = useState(false); // Keep for UI control

  // --- Settings Hook ---
  const {
    settings,
    setMode,
    setMaxLength,
    setSelectedTlds,
    setCreativityLevel,
    setTldMode,
    setDomainLanguage,
    setAvoidExpensiveTlds,
    hasCustomSettings,
    resetToDefaults: resetSettingsHook, // Rename to avoid conflict
    saveSettingsToStorage,
  } = useSearchSettings();

  // --- Read State from Zustand Store ---
  // --- Read State from Zustand Store ---
  // Removed: Loading/error states are no longer read here. Components use useDomainStore directly.
  // Removed: Store actions (clearDomains, clearErrors) are no longer called directly from here.

  // --- Actions ---

  const executeSearch = useCallback(
    async (submittedQueryOverride?: string) => {
      const finalQuery = submittedQueryOverride || query;
      if (!finalQuery) {
        console.warn('Search executed with empty query.');
        // Optionally show a toast or set an error state here if desired
        // toast.error(t('errors.descriptionRequired')); // Requires useTranslation
        return; // Prevent empty search
      }
      setSearchExecuted(true); // Mark that a search attempt was made
      saveSettingsToStorage(finalQuery); // Save settings on search execution
      // Delegate the actual search logic to the service
      await searchService.search(finalQuery, settings);
    },
    [query, settings, saveSettingsToStorage, setSearchExecuted]
  );

  const cancelSearch = useCallback(() => {
    searchService.cancelCurrentSearch();
    // Optionally update UI state if needed, though store state should reflect cancellation
  }, []);

  const clearCurrentSearch = useCallback(() => {
    setQuery('');
    setSearchExecuted(false);
    // Removed: clearDomains(); // Let components or service handle store clearing if needed
    // Removed: clearErrors(); // Let components or service handle store clearing if needed
    searchService.cancelCurrentSearch(); // Cancel any active search in the service
    clearSettingsFromStorage(); // Clear stored settings
    // Note: resetSettingsToDefaults handles resetting the settings state itself
  }, [setQuery, setSearchExecuted]); // Removed store actions from dependencies

  const resetSettingsToDefaults = useCallback(() => {
    resetSettingsHook(); // Reset settings state via the hook
    clearCurrentSearch(); // Also clear the search query and results/errors
  }, [resetSettingsHook, clearCurrentSearch]);

  // Add fetchDomainDescription action
  const fetchDomainDescription = useCallback(
    async (domainName: string, language: string, userQuery: string) => {
      // Delegate to the service instance
      return searchService.fetchDomainDescription(
        domainName,
        language,
        userQuery
      );
    },
    [] // No dependencies as it just calls the singleton service
  );

  // --- Memoized Context Value ---
  const contextValue = useMemo(
    () => ({
      // UI State
      query,
      setQuery,
      showUnavailable,
      setShowUnavailable,
      searchExecuted,
      setSearchExecuted,
      // Settings
      settings,
      setMode,
      setMaxLength,
      setSelectedTlds,
      setCreativityLevel,
      setTldMode,
      setDomainLanguage,
      setAvoidExpensiveTlds,
      hasCustomSettings,
      // Removed Store State
      // Actions
      executeSearch,
      cancelSearch,
      clearCurrentSearch,
      resetSettingsToDefaults,
      fetchDomainDescription, // Add to context value
    }),
    [
      query,
      showUnavailable,
      searchExecuted,
      settings,
      setMode,
      setMaxLength,
      setSelectedTlds,
      setCreativityLevel,
      setTldMode,
      setDomainLanguage,
      setAvoidExpensiveTlds,
      hasCustomSettings,
      // Removed Store State from dependencies
      executeSearch,
      cancelSearch,
      clearCurrentSearch,
      resetSettingsToDefaults,
      fetchDomainDescription, // Add to dependency array
      setQuery, // Include setters used in actions/memo
      setShowUnavailable,
      setSearchExecuted,
    ]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

// --- Hook to use the context ---
export const useSearch = () => useContext(SearchContext);

// --- Removed Helper Functions ---
// processGeneratedDomains
// extractTldsFromResults
// calculateDomainAvailabilityStatus
